import React from 'react';
import { Button } from '../../ui/Button/Button';
import { Text } from '../../ui/Text/Text';
import { cn } from '../../../lib/cn';

/**
 * EmptyState component for displaying empty data states
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.icon] - Icon name (UI5 icon name)
 * @param {React.ReactNode} [props.iconComponent] - Custom icon component
 * @param {string} props.title - Empty state title
 * @param {string} [props.description] - Empty state description
 * @param {string} [props.actionLabel] - Action button label
 * @param {Function} [props.onAction] - Action button click handler
 * @param {string} [props.actionDesign='Emphasized'] - Action button design
 * @param {React.ReactNode} [props.children] - Custom content (replaces action button)
 * @returns {JSX.Element}
 *
 * @example
 * // Basic usage
 * <EmptyState
 *   icon="product"
 *   title="No services found"
 *   description="Try adjusting your search or filters"
 * />
 *
 * @example
 * // With action button
 * <EmptyState
 *   icon="add"
 *   title="No customers yet"
 *   description="Get started by adding your first customer"
 *   actionLabel="Add Customer"
 *   onAction={() => navigate('/customers/new')}
 * />
 *
 * @example
 * // With custom content
 * <EmptyState
 *   icon="search"
 *   title="No results"
 *   description="We couldn't find any matches for your search"
 * >
 *   <div className="flex gap-2">
 *     <Button onClick={handleClearSearch}>Clear Search</Button>
 *     <Button design="Transparent" onClick={handleReset}>Reset Filters</Button>
 *   </div>
 * </EmptyState>
 */
export function EmptyState({
  className,
  icon,
  iconComponent,
  title,
  description,
  actionLabel,
  onAction,
  actionDesign = 'Emphasized',
  children,
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center py-12 px-6',
        className
      )}
    >
      {/* Icon */}
      {iconComponent ? (
        <div className="mb-4">{iconComponent}</div>
      ) : icon ? (
        <div className="mb-4">
          <svg
            className="w-16 h-16 text-gray-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            {/* Generic empty icon - customize based on icon prop */}
            {icon === 'search' && (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            )}
            {icon === 'product' && (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            )}
            {icon === 'add' && (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 4v16m8-8H4"
              />
            )}
            {icon === 'document' && (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            )}
            {icon === 'folder' && (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
              />
            )}
            {icon === 'customer' && (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            )}
            {/* Default icon if not matched */}
            {!['search', 'product', 'add', 'document', 'folder', 'customer'].includes(icon) && (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
              />
            )}
          </svg>
        </div>
      ) : null}

      {/* Title */}
      <Text className="text-lg font-semibold text-gray-700 mb-2">
        {title}
      </Text>

      {/* Description */}
      {description && (
        <Text className="text-sm text-gray-500 mb-6 max-w-md">
          {description}
        </Text>
      )}

      {/* Action Button or Custom Content */}
      {children ? (
        <div className="mt-2">{children}</div>
      ) : actionLabel && onAction ? (
        <Button design={actionDesign} onClick={onAction}>
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}
